/**
 * CR-6403: the Supplier Site dropdown's two cascades.
 *
 * Both are named in the acceptance criteria as behaviour that must "remain
 * intact", but neither ever worked — the dropdown always listed every
 * supplier regardless of the Company Site or Supplier Country selected.
 *
 * Extracted into its own hook so SupplierPerformanceFilters stays under the
 * 500-line limit.
 */
import { useState, useEffect, useMemo } from 'react';
import { suppliersAPI, Site } from '../../../lib/api';
import type { SupplierPerformanceFilterValues } from '../SupplierPerformanceFilters';

export type SupplierSiteOption = Site & { company_name?: string };

/**
 * @param supplierSites - the full, unnarrowed supplier list
 * @param localFilters - the panel's pending filter state
 * @param setLocalFilters - used to drop a selection the cascades invalidate
 * @returns the supplier list the Supplier Site dropdown should offer
 */
export default function useSupplierSiteOptions(
  supplierSites: Site[],
  localFilters: SupplierPerformanceFilterValues,
  setLocalFilters: React.Dispatch<React.SetStateAction<SupplierPerformanceFilterValues>>,
): SupplierSiteOption[] {
  // CR-6403: "site → supplier" and "supplier country → supplier site" are both
  // named in the acceptance criteria, and neither ever worked — the Supplier
  // Site dropdown always listed every supplier regardless of the Company Site
  // or Supplier Country selected.
  //
  // Company Site narrowing has to come from the server: nothing in the supplier
  // list says which customer sites a supplier trades with. We reuse
  // /suppliers/profiles/?site=, which is the SAME endpoint and param the
  // performance table itself is scoped by, so the dropdown can never offer a
  // supplier the table won't show — or hide one it will.
  //
  // (/core/companies/for-contract-pricing/?customer= also narrows by site, but
  // CR-6166 restricts it to 'approved'/'preferred' relationships. Measured on
  // dev: 15 of the 30 suppliers in the table fall outside that set, so using it
  // here would have made half the table unfilterable.)
  //
  // null = no Company Site selected, i.e. no narrowing.
  const [siteScopedSupplierIds, setSiteScopedSupplierIds] = useState<Set<number> | null>(null);

  useEffect(() => {
    const siteId = localFilters.site;
    if (!siteId || siteId === 'all') {
      setSiteScopedSupplierIds(null);
      return;
    }
    let cancelled = false;
    suppliersAPI
      .getProfiles({ site: siteId, page_size: 1000, minimal: true })
      .then((res) => {
        if (cancelled) return;
        setSiteScopedSupplierIds(new Set((res.data.results || []).map((p) => p.company_id)));
      })
      // Fall back to no narrowing rather than an empty dropdown if the call fails.
      .catch(() => { if (!cancelled) setSiteScopedSupplierIds(null); });
    return () => { cancelled = true; };
  }, [localFilters.site]);

  // Supplier Country narrows client-side — country already rides along on each
  // supplier record (it is what populates the Supplier Country options).
  const supplierSiteOptions = useMemo(() => {
    let list = supplierSites as Array<Site & { company_name?: string }>;
    if (siteScopedSupplierIds) {
      list = list.filter((s) => siteScopedSupplierIds.has(Number(s.id)));
    }
    const country = localFilters.supplierCountry;
    // The non-Helix branch builds supplierSites from /suppliers/profiles/,
    // which carries no country — so country is structurally absent there and
    // the Supplier Country dropdown has no options to pick. Guard on the data
    // actually existing: without this, a supplierCountry arriving from the
    // parent would filter every option away and leave an empty dropdown.
    const hasCountryData = list.some((s) => s.country);
    if (country && country !== 'all' && hasCountryData) {
      list = list.filter((s) => s.country === country);
    }
    return list;
  }, [supplierSites, siteScopedSupplierIds, localFilters.supplierCountry]);

  // A supplier already picked before the cascade narrowed can fall outside the
  // new list; leaving it selected would contradict the other two filters and
  // return nothing. Handled as an effect rather than in the onChange handlers
  // because the Company Site narrowing resolves asynchronously.
  useEffect(() => {
    if (localFilters.supplierSite === 'all') return;
    if (supplierSiteOptions.some((s) => String(s.id) === localFilters.supplierSite)) return;
    setLocalFilters((prev) => ({ ...prev, supplierSite: 'all' }));
  }, [supplierSiteOptions, localFilters.supplierSite]);

  return supplierSiteOptions;
}
